/**
 * handleAuthorize.js
 *
 * Copyright (c) apploft. GmbH
 */

import {publicKey} from '../../assets/apploft-cloud-stage-pubkey';
import {http, Authorization} from 'apploft-cloud-client';
import {authorizing} from '../PortalStateSymbol';
import {AuthorizationMethod} from './authorization-method';
import EncryptedCredentialsAuthorizationProvider from './encrypted-credentials-authorization-provider';

import {
  authorizationSucceeded,
  authorizationFailed,
} from '../PortalEventSymbol';

function createBasicAuthorization({username, password}) {
  const credentials = btoa([username, password].join(':'));

  return `Basic ${credentials}`;
}

function createUrl({schemes: [schema], host, basePath}) {
  return `${schema}://${host}${basePath}/frontendConfig`;
}

const AUTHORIZATION_METHOD = AuthorizationMethod.ENCRYPTED_CREDENTIALS;

function createEncryptedCredentialsAuthorizationMethod() {
  const authProvider = new EncryptedCredentialsAuthorizationProvider(
    getEncryptedCredentialsPublicKey(),
  );
  return {
    authorizationProvider: credentials =>
      authProvider.createAuthorization(credentials),
    authorizationRequestExecutor: postAuthorizationRequestExecutor,
  };
}

function getAuthorizeMethod(api) {
  // TODO determine authorization method from api spec somehow(?)
  switch (AUTHORIZATION_METHOD) {
    case AuthorizationMethod.ENCRYPTED_CREDENTIALS:
      return createEncryptedCredentialsAuthorizationMethod();
    case AuthorizationMethod.BASIC:
      return {
        authorizationProvider: createBasicAuthorization,
        authorizationRequestExecutor: http.get,
      };
    default:
      return {};
  }
}

function getEncryptedCredentialsPublicKey() {
  return publicKey;
}

export function postAuthorizationRequestExecutor({url, authorization, signal}) {
  return http.post({
    url,
    data: authorization,
    authorization: undefined,
    signal,
  });
}

export default function handleAuthorize({formData, dispatch, api}) {
  const authorizeMethod = getAuthorizeMethod(api);
  return handleAuthorizeWithMethod({formData, dispatch, api, authorizeMethod});
}

/**
 * @typedef {{username: string, password: string}} Credentials
 * @typedef {(credentials: Credentials) => string | object} AuthProvider
 * @typedef {{url: string, authorization: string, signal: AbortSignal}} AuthReqParams
 * @typedef {(params: AuthReqParams) => Promise<any>} AuthReqExecutor
 * @typedef {{authorizationProvider?: AuthProvider, authorizationRequestExecutor?: AuthReqExecutor}} AuthMethod
 * @param {{formData: Credentials, dispatch: Function, api: object, authorizeMethod: AuthMethod}} params
 */
export function handleAuthorizeWithMethod({
  formData: {username, password},
  dispatch,
  api,
  // TODO: Why do we need defaults?
  authorizeMethod: {
    authorizationProvider = createBasicAuthorization,
    authorizationRequestExecutor = http.get,
  },
}) {
  const url = createUrl(api);
  const controller = new AbortController();

  const {signal} = controller;
  const authorization = authorizationProvider({username, password});

  authorizationRequestExecutor({url, authorization, signal})
    .then(res => {
      const keyAdminAuth = Authorization.adminToken(res);

      if (!keyAdminAuth) {
        dispatch({event: authorizationFailed});

        return;
      }

      dispatch({
        event: authorizationSucceeded,
        payload: {keyAdminAuth, dispatch},
      });
    })
    .catch(error => {
      if (error instanceof DOMException) {
        console.assert(
          error.message === 'The user aborted a request.',
          `unexpected error: ${error.message}`,
        );
      }

      dispatch({event: authorizationFailed});
    });

  return {
    state: authorizing,
    controller,
  };
}
